<template>
  <div class="max-w-full mx-auto p-4">
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-gray-400" />
    </div>
    
    <div v-else-if="transactions.length === 0" class="flex flex-col items-center justify-center py-12 text-gray-500">
      <UIcon name="i-heroicons-clock" class="w-12 h-12 mb-3 text-gray-300" />
      <p class="text-sm">{{ t('eventHistory.noRecords') }}</p>
    </div>
    
    <template v-else>
      <div class="space-y-1">
        <div v-for="(transaction, index) in transactions" :key="transaction.id" class="relative">
          <div v-if="index < transactions.length - 1" class="absolute left-[15px] top-9 bottom-0 w-0.5 bg-gradient-to-b from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800"></div>
          
          <div class="flex items-center gap-3 mb-2">
            <div class="relative flex-shrink-0 z-[1]">
              <NameAvatar :fullName="transaction.author.name" :size="30" customColor="#C8017D" />
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium text-sm text-gray-900 dark:text-white">
                {{ transaction.author.name }}
              </span>
              <span class="text-xs text-gray-400">•</span>
              <span class="text-xs text-gray-500">
                {{ formatDate(transaction.timestamp, 'short', locale) }}
              </span>
              <span class="text-xs text-gray-400">•</span>
              <span class="text-xs text-gray-500">
                {{ formatDate(transaction.timestamp, 'time', locale) }}
              </span>
            </div>
          </div>
          
          <div class="ml-[39px] mb-4">
            <div class="bg-(--ui-bg-muted) rounded-lg p-3 border border-gray-200 dark:border-gray-600">
              <div class="space-y-3">
                <div
                  v-for="(entity, entityIndex) in transaction.entities"
                  :key="entityIndex"
                  :class="[
                    'pl-3 border-l-2',
                    entity.operation === 'Added' ? 'border-green-400 dark:border-green-500' : '',
                    entity.operation === 'Modified' ? 'border-amber-400 dark:border-amber-500' : '',
                    entity.operation === 'Deleted' ? 'border-red-400 dark:border-red-500' : ''
                  ]"
                >
                  <div class="flex items-center gap-2 mb-1">
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {{ entity.className }}
                    </span>
                    <span
                      :class="[
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
                        entity.operation === 'Added' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' : '',
                        entity.operation === 'Modified' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' : '',
                        entity.operation === 'Deleted' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' : ''
                      ]"
                    >
                      <UIcon :name="entity.operation === 'Added' ? 'i-heroicons-plus' : entity.operation === 'Deleted' ? 'i-heroicons-minus' : 'i-heroicons-pencil'" class="w-3 h-3" />
                      {{ entity.operation }}
                    </span>
                  </div>

                  <div v-if="entity.changes && entity.changes.length > 0" class="space-y-1.5 mt-2">
                    <div v-for="change in entity.changes" :key="change.field" class="flex items-start gap-2 text-xs">
                      <span class="text-gray-600 dark:text-gray-400 font-medium min-w-24 shrink-0">{{ change.field }}</span>
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <UTooltip v-if="change.oldValue" :text="change.oldValue" :delay-duration="300">
                          <span class="px-1.5 py-0.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded border border-red-200 dark:border-red-800 max-w-xs whitespace-pre-wrap">
                            {{ change.oldValue }}
                          </span>
                        </UTooltip>
                        <UIcon v-if="change.oldValue && change.newValue" name="i-heroicons-arrow-right" class="text-gray-400 w-3 h-3 shrink-0" />
                        <UTooltip v-if="change.newValue" :text="change.newValue" :delay-duration="300">
                          <span class="px-1.5 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded border border-green-200 dark:border-green-800 max-w-xs whitespace-pre-wrap">
                            {{ change.newValue }}
                          </span>
                        </UTooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="totalItems > pageSize" class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
        <div class="flex items-center justify-end">
          <UPagination
            v-model:page="currentPage"
            :total="totalItems"
            :items-per-page="pageSize"
            :max="5"
            :disabled="isLoading"
            :ui="{
              item: 'cursor-pointer',
              prev: 'cursor-pointer',
              next: 'cursor-pointer'
            }"
            @update:page="handlePageChange"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, type AuditEntryDtoAuditEntryDto } from '@/api/eOrdersApi'
import { formatDate } from '@/utils/date'

const { t, locale } = useI18n()

const props = defineProps<{
  externalReference: string
}>()

interface EntityChange {
  field: string
  oldValue: string | null
  newValue: string | null
}

interface EntityEntry {
  className: string
  operation: string
  changes: EntityChange[]
}

interface TransactionHistory {
  id: string
  timestamp: Date
  author: {
    name: string
  }
  entities: EntityEntry[]
}

const currentPage = ref(1)
const pageSize = 15
const isLoading = ref(false)
const allTransactions = ref<TransactionHistory[]>([])

const transactions = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  
  return allTransactions.value.slice(start, start + pageSize)
})

const totalItems = computed(() => allTransactions.value.length)

const ignoredProperties = new Set([
  'externalreference',
  'createddate',
  'modifieddate',
  'workflowexternalreference',
  'workflowmodifieddate',
  'transactionid',
  'createdbyid',
  'modifiedbyid',
  'classname',
  'id',
  'quotationnumber',
  'deliveryid',
  'purchaseordernumber',
  '_currentclientapprovalstep'
])

function isIgnoredProperty(propertyName: string): boolean {
  return ignoredProperties.has(propertyName.toLowerCase())
}

function getApprovalStepLabel(stepType: string): string {
  const key = `workflow.approval.stepTypes.${stepType[0].toLowerCase()}${stepType.slice(1)}`
  const translated = t(key)
  
  return translated !== key ? translated : stepType
}

function formatApprovalStep(step: Record<string, unknown>): string {
  const stepType = step.$type as string
  const label = getApprovalStepLabel(stepType)
  
  if (step.Email) {
    return `${label}: ${step.Email}`
  }
  
  if (step.GroupPath) {
    return `${label}: ${step.GroupPath}`
  }
  
  if (step.KeycloakId) {
    const id = step.KeycloakId as string
    return `${label}: ${id.substring(0, 8)}...`
  }
  
  return label
}

function isApprovalStepsArray(value: unknown): value is Array<Record<string, unknown>> {
  if (!Array.isArray(value) || value.length === 0) {
    return false
  }
  
  return value.every(item => typeof item === 'object' && item !== null && '$type' in item && typeof (item as Record<string, unknown>).$type === 'string' && ((item as Record<string, unknown>).$type as string).endsWith('ApprovalStep'))
}

function formatApprovalSteps(steps: Array<Record<string, unknown>>): string {
  return steps.sort((a, b) => (a.Order as number) - (b.Order as number)).map((step, index) => `${index + 1}. ${formatApprovalStep(step)}`).join('\n')
}

function formatChangeValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null
  }
  
  if (isApprovalStepsArray(value)) {
    return formatApprovalSteps(value)
  }
  
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  
  const stringValue = value.toString()
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/
  
  if (isoDatePattern.test(stringValue)) {
    return formatDate(stringValue, 'short', locale.value)
  }
  
  return stringValue
}

function groupByTransactionId(entries: AuditEntryDtoAuditEntryDto[]): TransactionHistory[] {
  const transactionGroups = new Map<string, AuditEntryDtoAuditEntryDto[]>()
  
  for (const entry of entries) {
    const existing = transactionGroups.get(entry.transactionId)
    
    if (existing) {
      existing.push(entry)
    } else {
      transactionGroups.set(entry.transactionId, [entry])
    }
  }
  
  const result: TransactionHistory[] = []
  
  for (const [transactionId, transactionEntries] of transactionGroups.entries()) {
    const firstEntry = transactionEntries[0]
    const entityGroups = new Map<string, AuditEntryDtoAuditEntryDto[]>()
    
    for (const entry of transactionEntries) {
      const key = `${entry.className}|${entry.operation}`
      const existing = entityGroups.get(key)
      
      if (existing) {
        existing.push(entry)
      } else {
        entityGroups.set(key, [entry])
      }
    }
    
    const entities: EntityEntry[] = []
    
    for (const [key, entityEntries] of entityGroups.entries()) {
      const [, operation] = key.split('|')
      const entityFirstEntry = entityEntries[0]
      const changes: EntityChange[] = []
      
      for (const entry of entityEntries) {
        if (entry.changeEvent) {
          for (const change of entry.changeEvent) {
            if (!isIgnoredProperty(change.propertyName)) {
              changes.push({
                field: change.propertyName,
                oldValue: formatChangeValue(change.oldValue),
                newValue: formatChangeValue(change.newValue)
              })
            }
          }
        }
      }
      
      if (operation === 'Modified' && changes.length === 0) {
        continue
      }

      entities.push({
        className: entityFirstEntry.className,
        operation: entityFirstEntry.operation,
        changes
      })
    }
    
    if (entities.length === 0) {
      continue
    }
    
    result.push({
      id: transactionId,
      timestamp: new Date(firstEntry.createdDate),
      author: {
        name: firstEntry.createdByFullName || 'System'
      },
      entities
    })
  }
  
  return result
}

async function fetchAuditEntries() {
  if (!props.externalReference) return
  
  isLoading.value = true
  
  try {
    const allEntries: AuditEntryDtoAuditEntryDto[] = []
    let page = 1
    let hasMore = true

    while (hasMore) {
      const response = await api.getAuditEntriesPaginated(
        page,
        100,
        [props.externalReference],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'createddate:desc'
      )
      
      if (response.type === 'success' && response.data) {
        const entries = (response.data.data ?? []) as AuditEntryDtoAuditEntryDto[]
        
        allEntries.push(...entries)
        hasMore = page < (response.data.totalPages ?? 1)
        page++
      } else {
        hasMore = false
      }
    }
    
    allTransactions.value = groupByTransactionId(allEntries)
    currentPage.value = 1
  } finally {
    isLoading.value = false
  }
}

function handlePageChange(newPage: number) {
  currentPage.value = newPage
}

onMounted(() => {
  fetchAuditEntries()
})

watch(() => props.externalReference, () => {
  currentPage.value = 1
  fetchAuditEntries()
})
</script>